// hud-seams.ts — the SHARED in-game PERSISTENT-OVERLAY module: the mount/unmount glue for
// the HUD BOTTOM BAR (credits/power + MENU button) + the SimUI OVERLAY TEXT LABELS (objective /
// alert / countdown / FPS) + the RADAR (minimap). A sibling of command-ui.ts.
//
// This is the EXACT mount/unmount logic that lived inline in client/src/web/main.ts (the
// hudSeam / simUISeam / minimapSeam blocks), lifted into one reusable factory so BOTH the
// shipped client AND the mission editor's Play host can stand up the REAL persistent in-game UI
// from a playtest session — instead of each host re-deriving it. main.ts adopts this later; for
// now the editor Play host is the first consumer.
//
// Unlike main.ts's seams (which gate on an opening CUTSCENE and reach the running game through
// gameHost.runningGame()), these reach the LIVE session through a single getSession() dep and
// skip the cutscene gate — the editor has no cutscene and a single session.
//
// Each seam OWNS its own root <div> (the SAME ids main.ts used — #dom-hud, #dom-simui,
// #dom-minimap) and its own view instance: it LAZILY mounts on the first push (when a session is
// live and its per-game getters are known), then forwards each frame's model. The HUD bottom bar
// + the overlay labels are SESSION OPTS (domHud / domSimUI — the session pushes their models from
// its update loop); the minimap is HOST-PULLED per frame (syncMinimap). teardownAll() unmounts
// all three idempotently (game-over / host close). Framework-free DOM, matching command-ui.ts.

import type { TexAtlasMgr } from '../../gfx/texatlasmgr.ts';
import type { Font } from '../../gfx/font.ts';
import {
  extractAtlasArt,
  titleArtToDataUrl,
  extractButtonArt,
  type ButtonArt,
} from '../title-art.ts';
import { HudView } from './hud-view.ts';
import type { HudState } from './hud-format.ts';
import { SimUIView, type SimUIModel } from './simui-view.ts';
import { MiniMapView, type MiniMapModel } from './minimap-view.ts';

/** The minimal slice of GameSession the seams pull each frame: the per-game HUD/SimUI getters
 *  (read once at lazy mount) + the minimap model/setViewPos (the minimap is host-pulled). The
 *  full GameSession satisfies this structurally, so a host passes its live session directly. */
export interface HudSeamsSession {
  /** apuntc[kutReactor].nPowerSupply — the units-per-pip the HUD power meter uses. */
  powerSupplyPerPip(): number;
  /** the REAL kifntHud bitmap font the HUD readouts render through. */
  hudFont(): Font;
  /** the canvas console (input) strip height in GAME px — the HUD bar / minimap anchor off it. */
  consoleStripHeight(): number;
  /** the kifntShadow line height in GAME px — the SimUI labels size to it. */
  simUiFontHeight(): number;
  /** the per-frame minimap model the radar consumes (host-pulled in syncMinimap). */
  minimapModel(): MiniMapModel;
  /** click-to-jump / drag-scroll → sui.SetViewPos (the SAME seam the canvas radar drove). */
  minimapSetViewPos(wx: number, wy: number): void;
}

/** Host-provided art + scale + sfx + the live-session accessor for the persistent overlays. */
export interface HudSeamsDeps {
  /** the texture atlas mgr — sources the MENU button bitmaps (menuup/menudown.png) and (via
   *  extractButtonArt) the 9-slice frame fallback. */
  tam: TexAtlasMgr;
  /** the CHROME magnification (= main.ts kUiMagnify): the HUD bar + its art is drawn at game px ×
   *  this, decoupled from the map scale so the bar + tap targets stay legible on phones. */
  magnify: number;
  /** the MAP present scale (= main.ts kGameScale): game px → CSS px for the SimUI labels (so they
   *  land at the canvas position) + the minimap (presented at × this). */
  gameScale: number;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr (no-op headless). */
  playSfx: (sfx: number) => void;
  /** the element the overlay root mounts into (default document.body). The root is
   *  `position:fixed; inset:0`; a TRANSFORMED pane-covering host element bounds it to the
   *  pane instead of the window (the mission editor's play-in-place WYSIWYG mode). */
  host?: HTMLElement;
  /** optional pre-extracted 9-slice button art (the MENU button's frame fallback). If omitted it
   *  is extracted once via extractButtonArt(tam). */
  objBtnArt?: ButtonArt | null;
  /** reach the LIVE session for the per-game getters + the minimap model/setViewPos. Returns null
   *  when no game is up (a stray push / pre-launch) → the seams no-op (nothing to mount over). */
  getSession: () => HudSeamsSession | null;
  /** the HUD MENU button → the host's in-game menu / exit-play seam. ABSENT ⇒ the MENU
   *  button is HIDDEN (a dead button reads as broken — the editor playtest exits via its
   *  own Stop control instead). */
  onMenu?: () => void;
  /** the credits-readout tap → the host's objectives briefing. Default no-op. */
  onCreditsTap?: () => void;
  /** the power-readout TAP-HOLD → the host's debug/test-options panel. Default no-op. */
  onPowerTapHold?: () => void;
  /** the ATTACK-MOVE toggle button → arm/disarm attack-move on the running game's SimUI.
   *  Default no-op (the button is still shown; it just does nothing if unwired). */
  onAttackMoveToggle?: () => void;
  /** optional SUPPRESS gate (= main.ts () => domCutSceneActive): while it returns true the
   *  overlays do NOT mount/refresh — the opening CUTSCENE plays over black with no HUD/labels/
   *  radar behind it; they mount on the first push once it clears. Default: never suppressed
   *  (the editor playtest has no cutscene). */
  suppressed?: () => boolean;
}

/** The mounted persistent overlays: the two session-opt handlers (domHud / domSimUI), the
 *  host-pulled minimap sync, + an idempotent teardown-all. */
export interface HudSeams {
  /** the domHud session opt (gameSessionOpts.domHud) — the session pushes a HudState per frame;
   *  the first push lazily mounts the REAL HudView, then forwards the state. */
  domHud: { update(state: HudState): void };
  /** the domSimUI session opt (gameSessionOpts.domSimUI) — the session pushes a SimUIModel per
   *  frame; the first push lazily mounts the REAL SimUIView, then forwards the model. */
  domSimUI: { update(model: SimUIModel): void };
  /** drive the RADAR — call once per frame from the host loop (after present). Lazily mounts the
   *  MiniMapView on the first push + forwards minimapModel(); a no-op when no session is live. */
  syncMinimap(): void;
  /** automation DISPLAY probe (wiUi.hudForceRender): push a synthetic HudState into the LIVE
   *  HudView to exercise pip states the running game may not hit at start. No-op (false) when the
   *  HUD isn't mounted — it does NOT mount one (the same semantics as the inline seam). */
  hudForceRender(state: HudState): boolean;
  /** per-overlay idempotent unmounts (a no-op when down) — main.ts uses these for the cutscene
   *  reveal gate (HUD/labels) + the per-frame game-over teardown. */
  unmountHud(): void;
  unmountSimUI(): void;
  unmountMinimap(): void;
  /** idempotently unmount all three overlays (game-over / host close). */
  teardownAll(): void;
}

/** Stand up the shared persistent in-game overlays for one play session. Each seam lazily owns a
 *  root <div> + a view instance; the first push mounts; teardownAll unmounts (idempotent). */
export function createHudSeams(deps: HudSeamsDeps): HudSeams {
  const { tam, magnify, gameScale, playSfx, getSession } = deps;
  const onMenu = deps.onMenu; // stays undefined when unwired → HudView hides the button
  const onCreditsTap = deps.onCreditsTap ?? ((): void => {});
  const onPowerTapHold = deps.onPowerTapHold ?? ((): void => {});
  const onAttackMoveToggle = deps.onAttackMoveToggle ?? ((): void => {});
  const suppressed = deps.suppressed ?? ((): boolean => false); // cutscene gate (main.ts), else never
  const playSfxFn = (sfx: number): void => {
    playSfx(sfx);
  };
  // the shared 9-slice button frame (the MENU button's fallback) — extracted once unless the host
  // already has it.
  const objBtnArt: ButtonArt | undefined = deps.objBtnArt ?? extractButtonArt(tam) ?? undefined;
  // the in-game MENU button's REAL bitmaps (menuup/menudown.png → data-URLs) — built the SAME way
  // main.ts does (extractAtlasArt → titleArtToDataUrl); absent ⇒ the 9-slice + web-label fallback.
  const menuUpArt = extractAtlasArt(tam, 'menuup.png');
  const menuDownArt = extractAtlasArt(tam, 'menudown.png');
  const hudMenuArt =
    menuUpArt !== null && menuDownArt !== null
      ? {
          up: titleArtToDataUrl(menuUpArt),
          down: titleArtToDataUrl(menuDownArt),
          cx: menuUpArt.cx,
          cy: menuUpArt.cy,
        }
      : undefined;

  // Create a hidden root <div id=id> appended to the body (the SAME ids main.ts used).
  function makeRoot(id: string): HTMLDivElement {
    const root = document.createElement('div');
    root.id = id;
    root.style.display = 'none';
    (deps.host ?? document.body).appendChild(root);
    return root;
  }

  // ── HUD bottom bar (#dom-hud) — the persistent credits/power + MENU bar (main.ts:2000) ────
  const hudRoot = makeRoot('dom-hud');
  let hudView: HudView | null = null;
  const unmountHud = (): void => {
    hudRoot.style.display = 'none';
    hudView?.destroy();
    hudView = null;
  };
  const domHud = {
    update: (state: HudState): void => {
      if (suppressed()) return; // no HUD bar behind the opening cutscene (mounts once it clears)
      if (hudView === null) {
        const gs = getSession();
        if (gs === null) return; // no game (a stray push) → nothing to mount over
        hudRoot.style.display = '';
        hudView = new HudView({
          root: hudRoot,
          onMenu,
          onCreditsTap,
          onPowerTapHold,
          onAttackMoveToggle,
          // the per-game power-per-pip + the REAL kifntHud bitmap font, read once at mount.
          powerSupplyPerPip: gs.powerSupplyPerPip(),
          hudFont: gs.hudFont(),
          // the chrome scale (kUiMagnify) — the bar is bigger than the map on phones. The bar
          // height = consoleStripPx × magnify.
          magnify,
          consoleStripPx: gs.consoleStripHeight(),
          // PREFER the canvas's real menu bitmap; fall back to the 9-slice frame + web "MENU".
          buttonArt: objBtnArt,
          menuArt: hudMenuArt,
          playSfx: playSfxFn,
        });
      }
      hudView.update(state);
    },
  };

  // ── SimUI overlay labels (#dom-simui) — objective / alert / countdown / FPS (main.ts:2065) ─
  const simUIRoot = makeRoot('dom-simui');
  let simUIView: SimUIView | null = null;
  const unmountSimUI = (): void => {
    simUIRoot.style.display = 'none';
    simUIView?.destroy();
    simUIView = null;
  };
  const domSimUI = {
    update: (model: SimUIModel): void => {
      if (suppressed()) return; // no overlay labels behind the opening cutscene
      if (simUIView === null) {
        const gs = getSession();
        if (gs === null) return; // no game (a stray push) → nothing to mount over
        simUIRoot.style.display = '';
        simUIView = new SimUIView({
          root: simUIRoot,
          // the canvas magnification (kGameScale) — the labels' game-px positions × this land at
          // the SAME screen position as the canvas SimUIForm text.
          magnify: gameScale,
          // the kifntShadow line height in game px — sizes the web text to match the canvas.
          fontPx: gs.simUiFontHeight(),
          // the bottom HUD bar height (game px) — the bottom-left alert toast is pinned ABOVE it.
          consoleStripPx: gs.consoleStripHeight(),
          // the DOM HUD bar's magnify (kUiMagnify) — the toast must clear the bar at THIS factor.
          hudMagnify: magnify,
          // gpdisp->GetSafeAreas (SimUI.cpp:363) — the all-zero stub the canvas SimUIForm reads.
          getSafeAreas: () => ({ left: 0, top: 0, right: 0, bottom: 0 }),
        });
      }
      simUIView.update(model);
    },
  };

  // ── minimap (#dom-minimap) — the in-game RADAR, host-pulled per frame (main.ts:2117) ──────
  const minimapRoot = makeRoot('dom-minimap');
  let minimapView: MiniMapView | null = null;
  const unmountMinimap = (): void => {
    minimapRoot.style.display = 'none';
    minimapView?.destroy();
    minimapView = null;
  };
  // call once per frame from the host loop: lazily mounts + forwards minimapModel(); a no-op when
  // no session is live (unlike the HUD/labels, the minimap model is HOST-pushed — the white
  // viewport box tracks scroll, which moves between sim ticks).
  const syncMinimap = (): void => {
    const gs = getSession();
    if (gs === null) {
      if (minimapView !== null) unmountMinimap();
      return;
    }
    if (suppressed()) return; // no radar behind the opening cutscene (leaves any mounted one as-is)
    if (minimapView === null) {
      minimapRoot.style.display = '';
      minimapView = new MiniMapView({
        root: minimapRoot,
        // the canvas magnification (kGameScale) — the radar dib presented at × this.
        magnify: gameScale,
        // the REAL DOM HUD bar height in CSS px — the radar sits directly above it. The bar is
        // consoleStripHeight × the CHROME magnify (kUiMagnify), which on phones is LARGER than
        // the canvas's own input strip (consoleStripHeight × kGameScale). Anchoring off kGameScale
        // here left the radar's bottom a few px INSIDE the taller bar (it "sat on" the bar's top
        // edge); use the chrome magnify so it clears the bar exactly.
        hudBarPx: gs.consoleStripHeight() * magnify,
        // click-to-jump / drag-scroll → sui.SetViewPos (the canvas MiniMapControl::OnPenEvent2).
        setViewPos: (wx: number, wy: number) => {
          getSession()?.minimapSetViewPos(wx, wy);
        },
      });
    }
    minimapView.update(gs.minimapModel());
  };

  // game-over / host close: unmount every overlay (idempotent — each unmount is a no-op when
  // nothing is mounted). Leaves the empty root divs in the DOM (cheap; reused on the next mount).
  const teardownAll = (): void => {
    unmountHud();
    unmountSimUI();
    unmountMinimap();
  };

  const hudForceRender = (state: HudState): boolean => {
    if (hudView === null) return false; // not mounted → no-op (does NOT mount one)
    hudView.update(state);
    return true;
  };

  return {
    domHud,
    domSimUI,
    syncMinimap,
    hudForceRender,
    unmountHud,
    unmountSimUI,
    unmountMinimap,
    teardownAll,
  };
}
